import { AccessDeniedError } from '@adornis/abac/errors/AccessDeniedError.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Student } from '../student.js';
import { Teacher } from '../teacher.js';
import { accessController, createEntityPayload, type EntityAction } from './policies.js';
import { getSubject, ownerIds, type Subject } from './subject.js';

type EntityRef = string | { _class: string; _collectionName?: string };

/** Every field a rule in policies.ts reads — always loaded, whatever projection the caller asks for. */
const POLICY_FIELDS = {
  _id: 1,
  userID: 1,
  userId: 1,
  examId: 1,
  teacherID: 1,
  studentID: 1,
  classRoomID: 1,
  conversationId: 1,
  ownerUserID: 1,
  readerUserIDs: 1,
} as const;

const className = (entity: EntityRef) => (typeof entity === 'string' ? entity : entity._class);

/** Throws `AccessDeniedError` unless the current subject may perform `action` on `doc`; returns `doc`. */
export async function assertCan<T>(entity: EntityRef, action: EntityAction, doc: T): Promise<T> {
  await accessController.assert(createEntityPayload(className(entity), action, doc));
  return doc;
}

export async function can(entity: EntityRef, action: EntityAction, doc: unknown): Promise<boolean> {
  return accessController.can(createEntityPayload(className(entity), action, doc));
}

/**
 * Loads a document by id and asserts `action` on it. A missing document is reported exactly like a foreign
 * one, so the error never tells a caller whether an id exists.
 */
export async function loadAndAssert<T = any>(
  entity: { _class: string; _collectionName: string },
  id: string,
  action: EntityAction,
  projection?: Record<string, unknown>,
): Promise<T> {
  const options = projection ? { projection: { ...projection, ...POLICY_FIELDS } } : undefined;
  const doc = typeof id === 'string' && id ? await (await getRawCollection(entity._collectionName)).findOne({ _id: id as any }, options) : null;
  if (!doc) throw new AccessDeniedError('Access denied: not found', { reason: `${entity._class} not found` });
  return assertCan(entity, action, doc as T);
}

/** `loadAndAssert` for a list of ids (e.g. the file ids an operation is asked to read). All or nothing. */
export async function loadAllAndAssert<T = any>(
  entity: { _class: string; _collectionName: string },
  ids: string[],
  action: EntityAction,
  projection?: Record<string, unknown>,
): Promise<T[]> {
  return Promise.all([...new Set(ids)].map(id => loadAndAssert<T>(entity, id, action, projection)));
}

/**
 * The list-query counterpart of the userID rules in policies.ts (Exam, Conversation, Submission, files):
 * the documents this subject owns. Matches nothing for an anonymous caller.
 */
export async function ownerFilter(field = 'userID'): Promise<Record<string, unknown>> {
  const ids = ownerIds(await getSubject());
  return ids.length ? { [field]: { $in: ids } } : { _id: { $in: [] } };
}

export async function requireUser(): Promise<Subject & { userID: string }> {
  const subject = await getSubject();
  if (!subject.userID) throw new AccessDeniedError('Access denied: not signed in', { reason: 'not signed in' });
  return subject as Subject & { userID: string };
}

/** A signed-in user or a session guest — the identity that owns userID-keyed documents. */
export async function requireOwnerIdentity(): Promise<Subject & { ownerID: string }> {
  const subject = await getSubject();
  const ownerID = subject.userID ?? subject.guestID;
  if (!ownerID) throw new AccessDeniedError('Access denied: no session identity', { reason: 'no identity' });
  return { ...subject, ownerID };
}

/**
 * The owner id for a document being created. In a client request it is ALWAYS the session's identity (user, else
 * session guest) — an owner passed in by the client is ignored, or anyone could write into any account. Only
 * server-context callers (jobs, which carry the exam owner in their payload) may name the owner themselves.
 */
export async function resolveOwnerID(ownerFromServerCaller?: string | null): Promise<string | null> {
  const subject = await getSubject();
  if (subject.isServer) return ownerFromServerCaller || null;
  return subject.userID ?? subject.guestID ?? null;
}

export async function requireTeacher(): Promise<Subject & { userID: string; teacherId: string }> {
  const subject = await requireUser();
  if (!subject.teacherId) throw new AccessDeniedError('Access denied: no teacher profile', { reason: 'not a teacher' });
  return subject as Subject & { userID: string; teacherId: string };
}

export async function requireStudent(): Promise<Subject & { userID: string; studentId: string }> {
  const subject = await requireUser();
  if (!subject.studentId) throw new AccessDeniedError('Access denied: no student profile', { reason: 'not a student' });
  return subject as Subject & { userID: string; studentId: string };
}

/** One profile per account, teacher OR student — the server resolves the role from whichever exists. */
export async function assertNoProfileYet(userID: string): Promise<void> {
  const [teacher, student] = await Promise.all([
    (await getRawCollection(Teacher._collectionName)).findOne({ userID }, { projection: { _id: 1 } }),
    (await getRawCollection(Student._collectionName)).findOne({ userID }, { projection: { _id: 1 } }),
  ]);
  if (teacher || student) throw new AccessDeniedError('Access denied: profile already exists', { reason: 'profile exists' });
}
